import { Component, ViewChild } from '@angular/core';
import { ReflexEnvironment as ENV, ResourceListPane } from '@smartsoftware/reflex-core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { 
    InvoiceFilterDataSource, 
    PagedDataSource, 
    PagedResourceListComponent, 
    Invoice_Service, 
    Invoice,
    ClientBranch_Service,
    UserPermission_Service,
    UserDevicePreferencesService
} from 'legalreflex-lib';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { BreadCrumbService } from '../../../components/breadcrumb-bar/breadcrumb-service';
import { FormBuilder } from '@angular/forms';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';


@Component({
    templateUrl: './list.html',
    styleUrls: ['./list.scss'],
    providers: [
        { provide: PagedDataSource, useExisting: InvoiceFilterDataSource }
    ]
})
export class InvoiceList extends PagedResourceListComponent<Invoice> {
    @ViewChild('filter') filterPane: ResourceListPane = new ResourceListPane();
    public filterCollapsed: boolean = sessionStorage.getItem("filterCollapsed") == 'true';
    public columnsToDisplay = ['Download', 'invoiceNumber', 'invoiceDate', 'totalOrdersOnInvoice', 'revenueAmount', 'nonRevenueAmount', 'invoiceAmount', 'balanceRemaining'];
    public customerNumber: string = '0';
    public companyNumber: number = 0;
    public corpId: string = '';

    public missingCustomerOrCompany: boolean = false;
    public loaded: boolean = false;
    public failed: boolean = false;
    public invoiceCount: number = -1;

    private _destroy$: Subject<void> = new Subject<void>();
    private _valueChanges$: Subject<void> = new Subject<void>();

    constructor(
        public dataSource: InvoiceFilterDataSource,
        protected resourceService: Invoice_Service,
        private clientBranchService: ClientBranch_Service,
        protected auth: OidcSecurityService,
        private userPermissionService: UserPermission_Service,
		private breadCrumbService: BreadCrumbService,
        protected dialog: MatDialog, 
        public route: ActivatedRoute, 
        public router: Router,
        public fb: FormBuilder,
        protected userDevicePreferences: UserDevicePreferencesService
    )
    {
        super(dataSource, resourceService, dialog, route, router);
        this.breadCrumbService.breadcrumbs.next([
            { label: 'Invoice', url: '/invoice'},
            { label: 'Billing History' }
        ]);

        this.breadCrumbService.actions.next([
            {label:'Refresh', action: this.applyFilters.bind(this), icon:'refresh'}
        ]);
        this.filterFormGroup = this.fb.group({
            customerNumber: [this.customerNumber],
            companyNumber: [this.companyNumber],
            invoiceNumber: [''],
            startInvoiceDate: [''],
            endInvoiceDate: ['']
        })
        for (const control in this.filterFormGroup.controls) {
            if (control === 'customerNumber' || control === 'companyNumber') continue;
            this.filterFormGroup.controls[control].setValue(this.userDevicePreferences.fcFilter[control]);
        }
        Object.keys(this.dataSource.filters).forEach((key) => {
            if (key !== "customerNumber" && key !== "companyNumber") {
                this.dataSource.filters[key] = this.userDevicePreferences.fcFilter[key];
            }
        });

    }

    ngOnInit() {
        super.ngOnInit();

        this._valueChanges$
            .pipe(
                switchMap(() => this.filterFormGroup!.valueChanges),
                takeUntil(this._destroy$)
            )
            .subscribe((value) => {
                delete value['customerNumber'];
                delete value['companyNumber'];
                this.userDevicePreferences.fcFilter = value;
            });

        this.auth.isAuthenticated$.subscribe((authenticated) => {
            if(!authenticated.isAuthenticated) return;

            this.userPermissionService.userSync.subscribe((userSynced: boolean) => {
                if(!userSynced) return;
        
                if(this.userPermissionService.loggedInUser?.companyNumber &&
                    this.userPermissionService.loggedInUser?.customerNumber) {
                    this.customerNumber = this.userPermissionService.loggedInUser?.customerNumber;
                    this.companyNumber = this.userPermissionService.loggedInUser?.companyNumber;
                    if(this.userPermissionService.loggedInUser?.corpId) {
                        this.corpId = this.userPermissionService.loggedInUser?.corpId;
                        this.listClientBranches();
                    } else {
                        this.clearFilters();
                    }
                } else {
                    this.missingCustomerOrCompany = true;
                }
            });
        });
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        setTimeout(()=>{
            let filterClick = document.getElementById('filterPaneInvoice');
            if(filterClick && !filterClick.hasAttribute('filterClickAttached')){
                this.filterPaneClick(filterClick, this.filterPane);
            }
            
            this.dataSource.loading.subscribe((state)=>{
                document.getElementById('filterPaneInvoice')?.click();
                if(!state) {
                    this.dataSource.totalItems.subscribe((result)=>{
                        this.invoiceCount = result;
                        this.loaded = !state && this.invoiceCount != -1;
                    });
                }
            })
            this.onFilterSubmit();
        });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this._destroy$.next();
        this._destroy$.complete();
    }

    listClientBranches() {
        this.clientBranchService.findByCompanyNumberCustomerNumberAndCorpId(this.companyNumber, parseInt(this.customerNumber), this.corpId)
            .subscribe((result)=>{
                if(result && result[0] && result[0]?.masterCustomerNumber && result[0]?.masterCompanyNumber) {
                    this.customerNumber = result[0].masterCustomerNumber.toString();
                    this.companyNumber = result[0].masterCompanyNumber;
                }
                this.clearFilters();
            })
    }

    setEmptyFilters(reset: boolean = false) {
        this.loaded = false;
        this.filterFormGroup = this.fb.group({
            customerNumber: [this.customerNumber],
            companyNumber: [this.companyNumber],
            invoiceNumber: [''],
            startInvoiceDate: [''],
            endInvoiceDate: ['']
        });
        if (!reset) {
            for (const control in this.filterFormGroup.controls) {
                if (control === 'customerNumber' || control === 'companyNumber') continue;
                this.filterFormGroup.controls[control].setValue(this.userDevicePreferences.fcFilter[control]);
            }
            Object.keys(this.dataSource.filters).forEach((key) => {
                if (key !== "customerNumber" && key !== "companyNumber") {
                    this.dataSource.filters[key] = this.userDevicePreferences.fcFilter[key];
                }
            });
        }
        this._valueChanges$.next();
    }

    applyFilters() {
        this.onFilterSubmit();
        this.dataSource.failed.subscribe((result)=>{
            this.failed = result;
        });
    }

    clearFilters(reset: boolean = false) {
        this.setEmptyFilters(reset);
        this.applyFilters();
    }

    formatDate(date: Date) {
        let year = date.getUTCFullYear();
        let month = date.getUTCMonth()+1;
        let day = date.getUTCDate();
        return month.toString().padStart(2,'0') + '/' + day.toString().padStart(2,'0') + '/' + year;
    }

    downloadInvoice(invoice: Invoice): void {
        let url = ENV.config.invoiceDownloadUrl;
        let invoiceDate = new Date(invoice.invoiceDate);
        let year = invoiceDate.getUTCFullYear().toString();
        let month = (invoiceDate.getUTCMonth() + 1).toString().padStart(2,'0');
        let date = invoiceDate.getUTCDate().toString().padStart(2,'0');
        let fileName = 'omrinv_' + 
        invoice.companyNumber.toString().padStart(3,'0') + '_' +
        invoice.customerNumber.toString().padStart(5,'0') + '_' +
        invoice.invoiceNumber.toString().padStart(8,'0') + '_' +
        year + month + date + '.pdf';

        if(sessionStorage.Impersonate)
            window.open(url + '/admin/' + year + '/' + month + '/' + fileName);
        else
            window.open(url + '/app/' + year + '/' + month + '/' + fileName);
    }
}
